import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User } from '../types';
import api from '../api/client';
import Navbar from '../components/Navbar';
import './Chats.css';

// Debounce helper
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

interface Attachment {
  type: string;
  url: string;
  file_name?: string;
  mime_type?: string;
}

interface Message {
  id: string;
  avito_message_id?: string;
  bitrix24_message_id?: string;
  direction: 'avito_to_bitrix' | 'bitrix_to_avito';
  text: string;
  source: 'avito' | 'bitrix24';
  attachments: Attachment[];
  sent_at: string;
  created_at: string;
  is_delivered: boolean;
}

interface Conversation {
  id: string;
  avito_user_id: string;
  avito_chat_id: string;
  bitrix24_chat_id: string;
  bitrix24_user_id?: string;
  is_active: boolean;
  last_message_at?: string;
  created_at: string;
}

const Chats: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConv, setSelectedConv] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sseConnected, setSseConnected] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const scrollToBottom = debounce(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, 100);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get('/api/avito-bridge/conversations');
      setConversations(res.data || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    }
  }, []);

  // Load messages for selected conversation
  const loadMessages = useCallback(async (convId: string) => {
    try {
      const res = await api.get(`/api/avito-bridge/conversations/${convId}/messages`);
      setMessages(res.data || []);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  }, []);

  // SSE connection for real-time updates
  const debouncedLoadConversations = useCallback(
    debounce(() => loadConversations(), 2000),
    [loadConversations]
  );

  const connectSSE = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // SSE для уведомлений - используется для новых сообщений из Avito/Bitrix24
    const sseUrl = `/api/sse/notifications?token=${token}`;
    const es = new EventSource(sseUrl);

    es.onopen = () => setSseConnected(true);

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'notification' && data.data?.type === 'im_message') {
          // Новое сообщение из Avito или Bitrix24
          const msg = data.data.data;
          if (selectedConv && msg.conversation_id === selectedConv.id) {
            setMessages((prev) => [msg, ...prev]);
            scrollToBottom();
          }
          // Обновляем список conversation с debounce
          debouncedLoadConversations();
        }
      } catch (e) {
        // ignore parse errors
      }
    };

    es.onerror = () => {
      setSseConnected(false);
      es.close();
      // Reconnect after 5 seconds
      setTimeout(connectSSE, 5000);
    };

    eventSourceRef.current = es;
  }, [selectedConv, debouncedLoadConversations, scrollToBottom]);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) setUser(JSON.parse(userStr));
    loadConversations().finally(() => setLoading(false));
  }, [loadConversations]);

  useEffect(() => {
    connectSSE();
    return () => {
      eventSourceRef.current?.close();
    };
  }, [connectSSE]);

  useEffect(() => {
    if (selectedConv) {
      loadMessages(selectedConv.id);
    }
  }, [selectedConv, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSelectConv = (conv: Conversation) => {
    setSelectedConv(conv);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConv || sending) return;

    setSending(true);
    try {
      // Отправляем сообщение через Avito API
      // Bridge сохранит сообщение и отправит в Bitrix24
      await api.post('/api/avito-bridge/send', {
        conversation_id: selectedConv.id,
        text: newMessage.trim(),
      });
      setNewMessage('');
      // Перезагружаем сообщения
      loadMessages(selectedConv.id);
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Сегодня';
    if (date.toDateString() === yesterday.toDateString()) return 'Вчера';
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  const getMessagePreview = (msg: Message) => {
    if (msg.text) return msg.text.substring(0, 50) + (msg.text.length > 50 ? '...' : '');
    if (msg.attachments?.length > 0) return '📎 Файл';
    return '';
  };

  const getDirectionIcon = (direction: string) => {
    if (direction === 'avito_to_bitrix') return '→ Avito';
    return '← Bitrix24';
  };

  const getSourceLabel = (source: string) => {
    if (source === 'avito') return 'Avito';
    return 'Bitrix24';
  };

  if (loading) {
    return (
      <div className="chats-loading">
        <div className="spinner" />
        <p>Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="chats">
      {user && <Navbar user={user} />}

      <div className="chats-container">
        {/* Sidebar - Conversations List */}
        <aside className="chats-sidebar">
          <div className="chats-sidebar-header">
            <h2>Чаты</h2>
            <div className={`sse-indicator ${sseConnected ? 'sse-indicator--connected' : ''}`} title={sseConnected ? 'Подключено' : 'Отключено'} />
          </div>

          {conversations.length === 0 ? (
            <div className="chats-empty">
              <p>Нет активных чатов</p>
              <p className="chats-empty-hint">Свяжите Avito и Bitrix24 для начала работы</p>
            </div>
          ) : (
            <div className="chats-list">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  className={`chats-item ${selectedConv?.id === conv.id ? 'chats-item--active' : ''}`}
                  onClick={() => handleSelectConv(conv)}
                >
                  <div className="chats-item-avatar">
                    {getSourceLabel('avito').charAt(0)}
                  </div>
                  <div className="chats-item-content">
                    <div className="chats-item-header">
                      <span className="chats-item-name">Чат {conv.avito_chat_id.slice(0, 8)}</span>
                      {conv.last_message_at && (
                        <span className="chats-item-time">{formatDate(conv.last_message_at)}</span>
                      )}
                    </div>
                    <div className="chats-item-preview">
                      {conv.bitrix24_chat_id ? 'Avito ↔ Bitrix24' : 'Ожидает связи'}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </aside>

        {/* Main - Chat Detail */}
        <main className="chats-main">
          {selectedConv ? (
            <>
              <div className="chats-header">
                <div className="chats-header-info">
                  <h3>Чат {selectedConv.avito_chat_id.slice(0, 8)}</h3>
                  <div className="chats-header-meta">
                    <span className={`chats-badge chats-badge--${selectedConv.bitrix24_chat_id ? 'linked' : 'unlinked'}`}>
                      {selectedConv.bitrix24_chat_id ? 'Связан с Bitrix24' : 'Не связан'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="chats-messages">
                {messages.length === 0 ? (
                  <div className="chats-messages-empty">
                    <p>Нет сообщений</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`chats-message ${msg.direction === 'bitrix_to_avito' ? 'chats-message--incoming' : 'chats-message--outgoing'}`}
                    >
                      <div className="chats-message-bubble">
                        <div className="chats-message-meta">
                          <span className={`chats-message-source chats-message-source--${msg.source}`}>
                            {getSourceLabel(msg.source)}
                          </span>
                          <span className="chats-message-time">{formatTime(msg.sent_at || msg.created_at)}</span>
                        </div>

                        {msg.text && <div className="chats-message-text">{msg.text}</div>}

                        {msg.attachments && msg.attachments.length > 0 && (
                          <div className="chats-message-attachments">
                            {msg.attachments.map((att, i) => (
                              <a
                                key={i}
                                href={att.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="chats-message-attachment"
                              >
                                📎 {att.file_name || 'Файл'}
                              </a>
                            ))}
                          </div>
                        )}

                        <div className="chats-message-direction">{getDirectionIcon(msg.direction)}</div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="chats-input-area">
                {selectedConv.bitrix24_chat_id ? (
                  <div className="chats-input-wrapper">
                    <textarea
                      className="chats-input"
                      placeholder="Введите сообщение..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      rows={1}
                    />
                    <button
                      className="chats-send-btn"
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sending}
                    >
                      {sending ? '...' : '→'}
                    </button>
                  </div>
                ) : (
                  <div className="chats-input-hint">
                    Свяжите чат с Bitrix24 для отправки сообщений
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="chats-no-chat">
              <p>Выберите чат из списка</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Chats;
